import { Preferences } from "@capacitor/preferences";

/**
 * Generate a unique cart item ID based on product, variant, and customizations
 */
export const generateCartItemId = (product) => {
  const variantSku = product.variant?.sku || "";
  const customizationsKey = Object.keys(product.customizations || {})
    .sort()
    .map((key) => {
      const value = product.customizations[key];
      if (Array.isArray(value)) {
        // For checkbox arrays (legacy cart), sort by ID and join with comma
        const ids = value
          .map(v => v?.sku || v?.id || "")
          .sort()
          .join(",");
        return `${key}:[${ids}]`;
      }
      if (value && Array.isArray(value.selections)) {
        const ids = value.selections
          .map((v) => v?.sku || v?.id || "")
          .sort()
          .join(",");
        return `${key}:[${ids}]`;
      }
      // For single selections (radio/select)
      return `${key}:${value?.sku || value?.id || ""}`;
    })
    .join("|");
  
  return `${product.productId}_${variantSku}_${customizationsKey}`;
};

/**
 * Check if a product already exists in cart (same variant and customizations)
 */
export const findCartItem = (cart, product) => {
  if (!cart || !cart.products) return null;
  
  const itemId = generateCartItemId(product);
  return cart.products.find((item) => generateCartItemId(item) === itemId);
};

/**
 * Get pilot cart from storage
 */
export const getPilotCart = async () => {
  try {
    const existingCart = await Preferences.get({ key: 'pilotCart' });
    return existingCart.value ? JSON.parse(existingCart.value) : { products: [], totalAmount: 0, totalCount: 0 };
  } catch (error) {
    console.error("Error getting pilot cart:", error);
    return { products: [], totalAmount: 0, totalCount: 0 };
  }
};

/**
 * Save pilot cart to storage
 */
export const savePilotCart = async (cart) => {
  try {
    await Preferences.set({ key: 'pilotCart', value: JSON.stringify(cart) });
    return true;
  } catch (error) {
    console.error("Error saving pilot cart:", error);
    return false;
  }
};

/**
 * Add product to cart or update quantity if already exists
 */
export const addToPilotCart = async (product) => {
  try {
    const cart = await getPilotCart();
    const existingItem = findCartItem(cart, product);
    
    if (existingItem) {
      // Product already in cart, increase quantity
      existingItem.quantity += 1;
      cart.totalAmount = (cart.totalAmount || 0) + product.price;
      cart.totalCount = (cart.totalCount || 0) + 1;
    } else {
      // New product, add to cart
      cart.products.push(product);
      cart.totalAmount = (cart.totalAmount || 0) + product.price;
      cart.totalCount = (cart.totalCount || 0) + 1;
    }
    
    await savePilotCart(cart);
    return { success: true, cart };
  } catch (error) {
    console.error("Error adding to pilot cart:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update product quantity in cart
 */
export const updatePilotCartQuantity = async (product, quantityChange) => {
  try {
    const cart = await getPilotCart();
    const existingItem = findCartItem(cart, product);
    
    if (!existingItem) {
      return { success: false, error: "Product not found in cart" };
    }
    
    const newQuantity = existingItem.quantity + quantityChange;
    
    if (newQuantity <= 0) {
      // Remove item from cart
      cart.products = cart.products.filter(
        (item) => generateCartItemId(item) !== generateCartItemId(product)
      );
      cart.totalAmount = Math.max(0, (cart.totalAmount || 0) - (existingItem.price * existingItem.quantity));
      cart.totalCount = Math.max(0, (cart.totalCount || 0) - existingItem.quantity);
    } else {
      // Update quantity
      const priceDiff = existingItem.price * quantityChange;
      existingItem.quantity = newQuantity;
      cart.totalAmount = Math.max(0, (cart.totalAmount || 0) + priceDiff);
      cart.totalCount = Math.max(0, (cart.totalCount || 0) + quantityChange);
    }
    
    await savePilotCart(cart);
    return { success: true, cart };
  } catch (error) {
    console.error("Error updating pilot cart quantity:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove product from cart
 */
export const removeFromPilotCart = async (product) => {
  try {
    const cart = await getPilotCart();
    const existingItem = findCartItem(cart, product);
    
    if (!existingItem) {
      return { success: false, error: "Product not found in cart" };
    }
    
    cart.products = cart.products.filter(
      (item) => generateCartItemId(item) !== generateCartItemId(product)
    );
    cart.totalAmount = Math.max(0, (cart.totalAmount || 0) - (existingItem.price * existingItem.quantity));
    cart.totalCount = Math.max(0, (cart.totalCount || 0) - existingItem.quantity);
    
    await savePilotCart(cart);
    return { success: true, cart };
  } catch (error) {
    console.error("Error removing from pilot cart:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Clear pilot cart
 */
export const clearPilotCart = async () => {
  try {
    await Preferences.remove({ key: 'pilotCart' });
    return { success: true };
  } catch (error) {
    console.error("Error clearing pilot cart:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Save selected pilot store for order placement (cached after in-store check)
 */
export const savePilotStore = async (store) => {
  try {
    await Preferences.set({ key: 'pilotSelectedStore', value: JSON.stringify(store) });
    return true;
  } catch (error) {
    console.error("Error saving pilot store:", error);
    return false;
  }
};

/**
 * Get cached pilot store
 */
export const getPilotStore = async () => {
  try {
    const stored = await Preferences.get({ key: 'pilotSelectedStore' });
    return stored.value ? JSON.parse(stored.value) : null;
  } catch (error) {
    console.error("Error getting pilot store:", error);
    return null;
  }
};

/**
 * Get quantity of a product in cart
 */
export const getProductQuantityInCart = async (product) => {
  try {
    const cart = await getPilotCart();
    const existingItem = findCartItem(cart, product);
    return existingItem ? existingItem.quantity : 0;
  } catch (error) {
    console.error("Error getting product quantity:", error);
    return 0;
  }
};
